import { formatServiceDateYmd, gridSlotToTimeStart } from '@/api/CareScheduleAssign'
import { SLOT_INTERVAL_MINUTES } from '../constants'
import { allGridSlots } from './scheduleGrid'

/** Calendar date helpers shared by calendar / data-sync composables. */
export function startOfWeekSunday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  x.setDate(x.getDate() - x.getDay())
  return x
}

/** 与 `startOfWeekSunday` 配对：当周周六（含） */
export function endOfWeekSaturday(d: Date): Date {
  const start = startOfWeekSunday(d)
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)
}

function scheduleGridDayStartTime24(): string {
  const first = allGridSlots()[0]
  return first ? gridSlotToTimeStart(first) : '08:00'
}

/** 末格起点 + 30min（与 Assign `time_end` 单格规则一致） */
function scheduleGridDayEndTime24(): string {
  const slots = allGridSlots()
  const last = slots.length > 0 ? slots[slots.length - 1] : undefined
  if (!last) return '20:00'
  const [hRaw, mRaw] = gridSlotToTimeStart(last).split(':')
  const h = Number(hRaw)
  const m = Number(mRaw)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '20:00'
  const totalMin = h * 60 + m + SLOT_INTERVAL_MINUTES
  const eh = Math.floor(totalMin / 60)
  const em = totalMin % 60
  return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`
}

function formatAvailabilityDateTime(date: Date, time24: string): string {
  return `${formatServiceDateYmd(date)} ${time24}`
}

/** availability 查询区间起始：当周周日 + 首格时间 */
export function formatAvailabilityWeekTimeStart(weekAnchor: Date): string {
  return formatAvailabilityDateTime(startOfWeekSunday(weekAnchor), scheduleGridDayStartTime24())
}

/** availability 查询区间结束：当周周六 + 末格结束时间 */
export function formatAvailabilityWeekTimeEnd(weekAnchor: Date): string {
  return formatAvailabilityDateTime(endOfWeekSaturday(weekAnchor), scheduleGridDayEndTime24())
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

export function isDateInWeekRange(d: Date, weekAnchorDate: Date): boolean {
  const start = startOfWeekSunday(weekAnchorDate)
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return day >= start && day <= end
}

export function ymdFromDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function parseServiceCycleDate(raw: string): Date | null {
  const s = raw.trim()
  if (!s || s === '—') return null
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    if (!Number.isNaN(d.getTime())) return d
  }
  const ymdSlash = s.match(/\b(\d{4})\/(\d{1,2})\/(\d{1,2})\b/)
  if (ymdSlash) {
    const d = new Date(Number(ymdSlash[1]), Number(ymdSlash[2]) - 1, Number(ymdSlash[3]))
    if (!Number.isNaN(d.getTime())) return d
  }
  if (/^pending\s+(schedule|reschedule|assignment)$/i.test(s)) return null
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const us = s.match(/([A-Za-z]{3})\s+(\d{1,2}),?\s*(\d{4})/i)
  if (us) {
    const mi = months.findIndex((x) => x.toLowerCase() === us[1]!.slice(0, 3).toLowerCase())
    if (mi >= 0) {
      const d = new Date(parseInt(us[3]!, 10), mi, parseInt(us[2]!, 10))
      if (!Number.isNaN(d.getTime())) return d
    }
  }
  return null
}

export function resolveServiceCycleDateFromOrderCard(
  o: import('@/api/orders').OrderCardDTO,
  lineIndex: number,
): Date | null {
  if (o.serviceDate?.trim()) {
    const d = parseServiceCycleDate(o.serviceDate)
    if (d) return d
  }
  const indices = [...new Set([lineIndex, ...o.services.map((_, i) => i)])]
  for (const idx of indices) {
    const line = o.services[idx]
    if (!line) continue
    const d = parseServiceCycleDate(line.scheduledAt ?? '')
    if (d) return d
  }
  return null
}

export type WeekDateAvailabilityTone = 'red' | 'blue' | 'green' | 'gray'

/** 当前周内首个 green/blue 可用日（跳过 past/gray/red） */
export function findFirstGreenOrBlueDateInWeek(
  weekDates: Date[],
  toneFor: (d: Date) => WeekDateAvailabilityTone,
): Date | null {
  for (const d of weekDates) {
    const tone = toneFor(d)
    if (tone === 'green' || tone === 'blue') return d
  }
  return null
}

function isBeforeCalendarDay(d: Date, ref: Date): boolean {
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const refDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate())
  return day.getTime() < refDay.getTime()
}

/** 当前周内从今天起首个 green/blue 可用日（跳过今天之前的日期） */
export function findFirstGreenOrBlueDateFromToday(
  weekDates: Date[],
  toneFor: (d: Date) => WeekDateAvailabilityTone,
  today: Date = new Date(),
): Date | null {
  for (const d of weekDates) {
    if (isBeforeCalendarDay(d, today)) continue
    const tone = toneFor(d)
    if (tone === 'green' || tone === 'blue') return d
  }
  return null
}

/** 从今天起首个仍有可点击时段的日期（由调用方提供 hasPickableSlot，与网格禁用规则一致） */
export function findFirstDateWithPickableSlotFromToday(
  weekDates: Date[],
  hasPickableSlot: (d: Date) => boolean,
  today: Date = new Date(),
): Date | null {
  for (const d of weekDates) {
    if (isBeforeCalendarDay(d, today)) continue
    if (hasPickableSlot(d)) return d
  }
  return null
}
